/**
 * O cliente HTTP da SPA — um lugar só.
 *
 * Três coisas que ele centraliza, e que espalhadas dariam três jeitos diferentes de
 * errar:
 *
 * 1. **O envelope de erro** (spec 03 §1.4). Toda falha da API chega como
 *    `ErroDaApi`, com `codigo`, `mensagem` (já em voz de produto, pronta para a
 *    tela) e `detalhes` por campo — que é o que acende o campo certo do formulário.
 * 2. **O contexto de entidade** (`X-DV-Entity` — DS-40): o seletor do cabeçalho vive
 *    no shell e a entidade viaja em TODO pedido, sem cada tela lembrar disso.
 * 3. **O cookie de sessão**: `credentials: 'include'`, sempre.
 *
 * O que ele NÃO faz: cache. Não há camada de dados esperta aqui — cada tela pede o
 * que precisa e recarrega depois de gravar. Numa tela de administração isso é
 * exatamente o comportamento certo: o que se quer ver depois de salvar é o que o
 * servidor gravou, não o que o navegador achou que tinha gravado.
 */
import type { CodigoDeErro, DetalheDeErro, EnvelopeDeErro } from '@dv/contracts'

const BASE = '/api/v1'

export class ErroDaApi extends Error {
  constructor(
    readonly codigo: CodigoDeErro,
    mensagem: string,
    readonly detalhes: readonly DetalheDeErro[] = [],
    readonly status = 0,
    readonly extra: Readonly<Record<string, unknown>> = {},
  ) {
    super(mensagem)
    this.name = 'ErroDaApi'
  }

  /** A mensagem do campo, quando a API a nomeou (spec 03 §1.4 `details`). */
  doCampo(campo: string): string | undefined {
    const regra = this.detalhes.find((d) => d.path === campo)?.rule
    if (regra === undefined) return undefined
    return /^[a-z][a-z0-9_]*(?:\.[a-z0-9_]+)+$/.test(regra) ? this.message : regra
  }
}

/**
 * "Não existe" (ou "não é seu") — o único erro que a tela pode traduzir em ausência.
 *
 * A distinção não é sutileza: qualquer outra falha significa que o registro
 * provavelmente EXISTE e não conseguimos lê-lo, e afirmar "não encontrado" nesse caso
 * é a tela mentindo. As fichas do cadastro fundiam os dois — rede fora virava
 * "Empresa não encontrada", sem sequer oferecer o "Tentar novamente".
 *
 * O 403 entra junto de propósito: em superfície externa, "não existe" e "não é seu"
 * têm a MESMA mensagem (lei de erros do repositório · spec 03 §1.4).
 */
export function ehNaoEncontrado(erro: ErroDaApi): boolean {
  return erro.codigo === 'NOT_FOUND' || erro.codigo === 'FORBIDDEN' || erro.status === 404 || erro.status === 403
}

/** A entidade ativa do cabeçalho. Definida pelo shell, lida por todo pedido. */
let entidadeAtiva: string | null = null

export function definirEntidadeDoContexto(id: string | null): void {
  entidadeAtiva = id
}

export interface OpcoesDePedido {
  readonly metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  readonly corpo?: unknown
  readonly sinal?: AbortSignal
}

export async function pedir<T>(caminho: string, opcoes: OpcoesDePedido = {}): Promise<T> {
  const metodo = opcoes.metodo ?? 'GET'
  const cabecalhos: Record<string, string> = { accept: 'application/json' }
  if (opcoes.corpo !== undefined) cabecalhos['content-type'] = 'application/json'
  if (entidadeAtiva !== null) cabecalhos['x-dv-entity'] = entidadeAtiva

  let resposta: Response
  try {
    resposta = await fetch(`${BASE}${caminho}`, {
      method: metodo,
      headers: cabecalhos,
      credentials: 'include',
      ...(opcoes.corpo === undefined ? {} : { body: JSON.stringify(opcoes.corpo) }),
      ...(opcoes.sinal === undefined ? {} : { signal: opcoes.sinal }),
    })
  } catch {
    // Rede fora do ar não tem envelope — mas a tela precisa de uma mensagem em voz
    // de produto do mesmo jeito.
    throw new ErroDaApi('INTERNAL', 'Não foi possível falar com o servidor. Verifique a conexão.')
  }

  const texto = await resposta.text()
  const corpo: unknown = texto === '' ? null : seguroJson(texto)

  if (!resposta.ok) {
    const envelope = corpo as Partial<EnvelopeDeErro> & Record<string, unknown>
    const erro = envelope.error
    throw new ErroDaApi(
      erro?.code ?? 'INTERNAL',
      erro?.message ?? 'Não foi possível concluir a operação.',
      erro?.details ?? [],
      resposta.status,
      corpo === null ? {} : (corpo as Record<string, unknown>),
    )
  }
  return corpo as T
}

/** Download autenticado com a mesma entidade e a mesma tradução de erro da API. */
export async function baixar(caminho: string): Promise<Blob> {
  const cabecalhos: Record<string, string> = { accept: 'text/csv' }
  if (entidadeAtiva !== null) cabecalhos['x-dv-entity'] = entidadeAtiva

  let resposta: Response
  try {
    resposta = await fetch(`${BASE}${caminho}`, {
      method: 'GET',
      headers: cabecalhos,
      credentials: 'include',
    })
  } catch {
    throw new ErroDaApi('INTERNAL', 'Não foi possível falar com o servidor. Verifique a conexão.')
  }

  const dados = await resposta.arrayBuffer()
  if (!resposta.ok) {
    const texto = new TextDecoder().decode(dados)
    const corpo = seguroJson(texto) as Partial<EnvelopeDeErro> & Record<string, unknown>
    const erro = corpo.error
    throw new ErroDaApi(
      erro?.code ?? 'INTERNAL',
      erro?.message ?? 'Não foi possível concluir a exportação.',
      erro?.details ?? [],
      resposta.status,
      corpo,
    )
  }
  return new Blob([dados], { type: resposta.headers.get('content-type') ?? 'application/octet-stream' })
}

/**
 * O upload de arquivo (passo 7.1) — `multipart/form-data`, e por isso não passa por `pedir`.
 *
 * Duas diferenças, e as duas importam:
 *
 * 1. **Não se declara `content-type`.** O navegador precisa escrevê-lo sozinho, porque
 *    é ele quem escolhe o `boundary` do multipart. Declarar `multipart/form-data` à mão
 *    manda um envelope sem fronteira, e o servidor não acha o arquivo.
 * 2. **O corpo não é serializado.** `FormData` vai como está; passá-lo por
 *    `JSON.stringify` daria `{}`, que é o tipo de defeito que só aparece em produção
 *    porque o teste manda o corpo montado à mão.
 *
 * O resto — cookie, contexto de entidade, tradução do envelope de erro — é o mesmo, e
 * fica aqui em vez de virar um segundo cliente HTTP com regras próprias.
 */
export async function enviarArquivo<T>(caminho: string, formulario: FormData): Promise<T> {
  const cabecalhos: Record<string, string> = { accept: 'application/json' }
  if (entidadeAtiva !== null) cabecalhos['x-dv-entity'] = entidadeAtiva

  let resposta: Response
  try {
    resposta = await fetch(`${BASE}${caminho}`, {
      method: 'POST',
      headers: cabecalhos,
      credentials: 'include',
      body: formulario,
    })
  } catch {
    throw new ErroDaApi('INTERNAL', 'Não foi possível falar com o servidor. Verifique a conexão.')
  }

  const texto = await resposta.text()
  const corpo: unknown = texto === '' ? null : seguroJson(texto)
  if (!resposta.ok) {
    const envelope = corpo as Partial<EnvelopeDeErro> & Record<string, unknown>
    const erro = envelope.error
    throw new ErroDaApi(
      erro?.code ?? 'INTERNAL',
      erro?.message ?? 'Não foi possível enviar o arquivo.',
      erro?.details ?? [],
      resposta.status,
    )
  }
  return corpo as T
}

function seguroJson(texto: string): unknown {
  try {
    return JSON.parse(texto)
  } catch {
    return null
  }
}
